import { ReactNode, useEffect } from "react";
import classnames from "classnames";
import { useRecoilState, useRecoilValue } from "recoil";

import { IMarket, Swarm, SwarmInstance } from "@lbs/swarm-client";

import { IAuthData, IBonusRule, ICurrencyConfig, IPartnerConfig, ISwarmConfig } from "src/constants/interfaces";
import { IVerificationResolver, IVerificationResponse } from "src/constants/interfaces/verification";
import selectionsStore, { storeLastBetSelections } from "src/stores/selections";
import { OddsFormat, TranslateFunc, decimalString } from "src/constants/types";
import { IFreeBonus, ISelection } from "src/constants/interfaces/bet";
import mainStore, { showSuccessStateSelector } from "src/stores/main";
import { useUpperDataProvider } from "src/hooks/upperDataProvider";
import { racingTypesList } from "src/constants/racingTypesList";
import { BetTypeViewEnum } from "src/constants/enums/betType";
import { useSelectionsSync } from "src/hooks/selectionsSync";
import { IClientConfig } from "src/client-config/config";
import StickyWrapper from "src/components/stickyWrapper";
import Communicator from "src/services/communicator";
import { usePrevious } from "src/hooks/previous";
import BookingId from "src/components/bookingId";
import SelectionsList from "../selectionsList";
import { initSwarm } from "src/common/swarm";
import { useConfig } from "src/hooks/config";

import BookingCheckBlock from "../bookingCheckBlock";
import SuccessState from "../successState";
import { resetBetslip } from "./helpers";
import BetBlock from "../betBlock";
import Popups from "../popups";
import Header from "../header";
import HotKeys from "../hotKeys";

import "./style.scss";

export interface IProps {
	stake?: decimalString;
	balance: number;
	userId: number;
	freeBonuses?: IFreeBonus[];
	swarmInstance?: SwarmInstance;
	translate?: TranslateFunc;
	config?: Partial<IClientConfig>;
	swarmConfig: ISwarmConfig;
	selections: ISelection[];
	authData: IAuthData;
	oddsFormat: OddsFormat;
	partnerConfig: IPartnerConfig;
	currencyConfig: ICurrencyConfig;
	bonusRules: IBonusRule[];
	profitBoostPercent: number | null;
	profitBoostStake: number | null;
	verificationResolver?: IVerificationResolver;
	verificationResponse?: IVerificationResponse | null;
	language: string;
	memberId: string;
	className?: string;
	topContent?: ReactNode;
	isMobile?: boolean;

	transformSelection?(selection: ISelection): ISelection;

	onSelectionsChange(selections: ISelection[]): void;
}

export const Betslip = (props: IProps) => {
	console.log('betslip main here')
	const { topContent, language, swarmConfig, selections, onSelectionsChange, swarmInstance, className = "", isMobile } = props;
	const prevLanguage = usePrevious(language);
	const config = useConfig();
	const [{ lastBetSelections }] = useRecoilState(selectionsStore);
	const [mainState, setMainState] = useRecoilState(mainStore);

	const showSuccessState = useRecoilValue(showSuccessStateSelector);

	useUpperDataProvider(props);
	useSelectionsSync(selections, lastBetSelections, onSelectionsChange);

	useEffect(() => {
		if (swarmInstance) Swarm.registerInstance(swarmInstance);
		else {
			const justOnce = !prevLanguage && language;
			justOnce && initSwarm(language, swarmConfig);
		}
	}, [swarmInstance, prevLanguage, language, swarmConfig]);

	useEffect(() => {
		if (!swarmInstance && prevLanguage && prevLanguage !== language)
			Swarm.getInstance().changeLanguage(language);
	}, [swarmInstance, prevLanguage, language]);

	useEffect(() => {
		Communicator.initListeners();
	}, []);

	const clearBetslip = (removingSelectionIds?: number[], saveAsLastSelections?: boolean) => {
		const allSelectionsRemoved = !removingSelectionIds || removingSelectionIds.length === selections.length;

		if (saveAsLastSelections) {
			storeLastBetSelections(selections);
		}

		if (allSelectionsRemoved) {
			onSelectionsChange([]);
			resetBetslip();
		} else {
			onSelectionsChange(selections.filter(item => !removingSelectionIds.includes(item.event.id)));
		}
	};

	const repeatBet = () => {
		onSelectionsChange(lastBetSelections);
		setMainState({ ...mainState, stake: mainState.lastBetStake });
	};

	return (
		<StickyWrapper selections={selections}>
			<div
				className={`B-betslip-wrapper ${!config.enableStickyMode ? 'B-betslip-static' : ''} ${className || "B-betslip-theme-dark"} ${mainState.placeBetLoading ? "B-betslip-wrapper-disable-actions" : ""
					}`}
			>
				<Popups />
				{topContent}
				<div className="B-betslip-holder">
					<div className="B-betslip-inner">
						<div className="B-betslip-top">
							<Header />
						</div>
						<div className="B-betslip-content-holder">
							<div
								className={classnames("B-betslip-content-block", {
									[BetTypeViewEnum[mainState.betType]]: true,
									"B-racing-type": racingTypesList.includes(
										mainState.betType
									),
									"B-hot-keys": (config.enableHotKeys && !showSuccessState ) || config.enableHotBookingId
								})}
							>
								{((config.enableHotKeys && !showSuccessState ) || config.enableHotBookingId) &&
									<div className="B-betslip-hot-keys-wrapper">
									{config.enableHotKeys && !showSuccessState && (
										<HotKeys onSelectionsChange={onSelectionsChange} isMobile={isMobile}/>
									)}
									{config.enableHotBookingId && (
										<BookingId isMobile={isMobile}/>
									)}
								</div>}
								{showSuccessState && !selections.length ? (
									<SuccessState onRepeatBet={repeatBet} />
								) : (
									<>
										<SelectionsList
											onChange={onSelectionsChange}
										/>

										{!!selections.length && (
											<BetBlock
												onClear={clearBetslip}
											/>
										)}

										{config.enableBookingId && (
											<BookingCheckBlock isMobile={isMobile} />
										)}
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</StickyWrapper>
	);
};

export default Betslip;
